import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { logEvent, analytics } from '../firebase.js';
import { CollectionService } from '../services/collection-service.js';
import { DesignService } from '../services/design-service.js';
import { ProfileService } from '../services/profile-service.js';
import { gridImage, fallbackToFull } from '../utils/design-image.js';
import { QueryCache } from '../services/cache.js';
import { shareLink } from '../services/share-service.js';
import { publicOrigin } from '../services/platform-service.js';
import { useLocale } from '../hooks/useLocale.jsx';

export function CollectionPage({ user }) {
  const { id } = useParams();
  const { t, styleName } = useLocale();
  const navigate = useNavigate();

  // Hydrate from cache so revisiting a collection (esp. via Bookmarks page →
  // collection card → back) doesn't flash spinner before content.
  const cachedCol = id ? QueryCache.get(`collection:${id}`) : null;
  const cachedItems = id ? QueryCache.get(`collectionItems:${id}`) : null;

  const [col, setCol] = useState(cachedCol || null);
  const [items, setItems] = useState(cachedItems || []);
  const [ownerProfile, setOwnerProfile] = useState(null);
  const [loading, setLoading] = useState(!cachedCol);
  const [notFound, setNotFound] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [nameInput, setNameInput] = useState(cachedCol?.name || '');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    let mounted = true;
    let unsub = () => {};
    // Only show spinner when we have nothing cached to show.
    if (!cachedCol) setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const c = await CollectionService.getById(id);
        if (!mounted) return;
        if (!c) { setNotFound(true); setLoading(false); return; }
        setCol(c);
        if (!nameInput) setNameInput(c.name || '');
        QueryCache.set(`collection:${id}`, c);

        // Subscribe to live updates for itemCount / cover changes.
        unsub = CollectionService.subscribe(id, (live) => {
          if (mounted && live) {
            setCol(live);
            QueryCache.set(`collection:${id}`, live);
          }
        });

        // Owner profile (for "by @handle")
        ProfileService.getByUid(c.ownerId).then(p => {
          if (mounted) setOwnerProfile(p);
        });

        // Resolve items into designs.
        const itemDocs = await CollectionService.listItems(id);
        const designs = await Promise.all(
          itemDocs.map(it => DesignService.getDesign(it.designId).catch(() => null)),
        );
        const filtered = designs.filter(Boolean);
        if (mounted) {
          setItems(filtered);
          QueryCache.set(`collectionItems:${id}`, filtered);
        }
      } catch (err) {
        console.error('collection page:', err);
        if (mounted && !cachedCol) setNotFound(true);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; unsub?.(); };
  }, [id]); // eslint-disable-line

  if (loading) return <main className="main"><div className="loading"><div className="spinner"></div></div></main>;
  if (notFound || !col) return (
    <main className="main">
      <p style={{ color: 'var(--text-secondary)' }}>{t('collectionNotFound')}</p>
      <div className="controls" style={{ justifyContent: 'flex-start' }}>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>{t('back')}</button>
      </div>
    </main>
  );

  const isOwner = user && !user.isAnonymous && user.uid === col.ownerId;

  const handleRename = async () => {
    if (!nameInput.trim() || nameInput === col.name) { setRenaming(false); return; }
    try {
      await CollectionService.rename(id, nameInput);
      logEvent(analytics, 'collection_renamed');
    } catch (err) {
      console.error('rename:', err);
    }
    setRenaming(false);
  };

  const handleDelete = async () => {
    try {
      await CollectionService.delete(id);
      QueryCache.del(`collection:${id}`);
      QueryCache.del(`collectionItems:${id}`);
      QueryCache.delByPrefix('myCollections:');
      logEvent(analytics, 'collection_deleted');
      navigate(ownerProfile?.handle ? `/u/${ownerProfile.handle}` : '/');
    } catch (err) {
      console.error('delete:', err);
      setConfirmDelete(false);
    }
  };

  const handleTogglePublic = async () => {
    try {
      await CollectionService.setPublic(id, !col.isPublic);
      logEvent(analytics, 'collection_visibility_changed', { isPublic: !col.isPublic });
    } catch (err) {
      console.error('toggle public:', err);
    }
  };

  const handleRemoveItem = async (designId) => {
    try {
      await CollectionService.removeItem(id, designId);
      setItems(prev => {
        const next = prev.filter(d => d.id !== designId);
        QueryCache.set(`collectionItems:${id}`, next);
        return next;
      });
      logEvent(analytics, 'collection_item_removed_from_page');
    } catch (err) {
      console.error('remove item:', err);
    }
  };

  const handleShare = async () => {
    const url = `${publicOrigin()}/c/${id}`;
    try {
      const shared = await shareLink({ title: col.name, url });
      if (!shared) {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      }
      logEvent(analytics, 'collection_shared');
    } catch (err) {
      if (err?.name !== 'AbortError') console.error('share:', err);
    }
  };

  return (
    <main className="main">
      {/* 모바일: back 을 액션 아이콘들과 같은 줄에 두고 제목은 그 아래 별도 줄.
          예전엔 MobileHeader 가 back 을 따로 그려 제목 위에 빈 행이 생기면서
          제목과 back 사이가 크게 벌어졌다. DesignDetail 과 같은 모양이다. */}
      <div className="collection-topbar">
        <button
          type="button"
          className="detail-back-btn"
          onClick={() => navigate(-1)}
          aria-label={t('back')}
        >
          <i className="material-symbols-rounded">arrow_back_ios_new</i>
          <span>{t('back')}</span>
        </button>
        <div className="collection-header-actions">
          <button
            type="button"
            className="detail-icon-btn"
            onClick={handleShare}
            title={linkCopied ? t('copied') : t('share')}
            aria-label={t('share')}
          >
            <i className="material-symbols-rounded">{linkCopied ? 'check' : 'ios_share'}</i>
          </button>
          {isOwner && (
            <>
              <button
                type="button"
                className="detail-icon-btn"
                onClick={() => setRenaming(true)}
                title={t('collectionRename')}
                aria-label={t('collectionRename')}
              >
                <i className="material-symbols-rounded">edit</i>
              </button>
              {/* 공개/비공개 자물쇠 토글은 감춤 — 프로필이 소유자의 컬렉션을
                 전부 보여주게 바뀌어서 이 버튼은 아무 효력이 없다. 그대로 두면
                 잠갔다고 착각하게 만든다. handleTogglePublic /
                 CollectionService.setPublic 은 그대로 남겨둠. */}
              <button
                type="button"
                className="detail-icon-btn danger-btn"
                onClick={() => setConfirmDelete(true)}
                title={t('collectionDelete')}
                aria-label={t('collectionDelete')}
              >
                <i className="material-symbols-rounded">delete</i>
              </button>
            </>
          )}
        </div>
      </div>
      <div className="collection-header">
        <div className="collection-header-meta">
          <h2 className="collection-title">{col.name}</h2>
          <p className="collection-subtitle">
            {ownerProfile?.handle && (
              <>
                {t('collectionByOwner')}{' '}
                <button
                  type="button"
                  className="link-button"
                  onClick={() => navigate(`/u/${ownerProfile.handle}`)}
                >
                  @{ownerProfile.handle}
                </button>
                {' · '}
              </>
            )}
            {t('collectionItemCount', { n: col.itemCount || 0 })}
            {/* '공개' 태그 제거 — 이제 모든 컬렉션이 프로필에서 보인다. */}
          </p>
        </div>
      </div>

      {items.length === 0 ? (
        <p style={{ color: 'var(--text-secondary)' }}>{t('collectionEmpty2')}</p>
      ) : (
        <div className="moodboard-grid">
          {items.map(d => {
            const thumb = gridImage(d);
            const styleLabel = styleName(d.style, d.spaceCategory);
            return (
              <div key={d.id} className="moodboard-item">
                <button
                  type="button"
                  className="moodboard-item-btn"
                  onClick={() => navigate(d.userId === user?.uid ? `/designs/${d.id}` : `/s/${d.id}`)}
                >
                  {thumb && <img src={thumb} alt={d.name || styleLabel} loading="lazy" onError={fallbackToFull(d)} />}
                </button>
                {isOwner && (
                  <button
                    type="button"
                    className="moodboard-remove-btn"
                    onClick={(e) => { e.stopPropagation(); handleRemoveItem(d.id); }}
                    title={t('collectionRemoveFromBoard')}
                    aria-label={t('collectionRemoveFromBoard')}
                  >
                    <i className="material-symbols-rounded">close</i>
                  </button>
                )}
                <div className="moodboard-item-meta">
                  <span>{d.name || styleLabel}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {renaming && (
        <div className="modal-overlay" onClick={() => setRenaming(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>{t('collectionRename')}</h3>
            <input
              className="rename-input"
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setRenaming(false); }}
            />
            <div className="controls" style={{ marginTop: '1rem' }}>
              <button className="btn btn-primary" onClick={handleRename}>{t('save')}</button>
              <button className="btn btn-secondary" onClick={() => setRenaming(false)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="modal-overlay" onClick={() => setConfirmDelete(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>{t('collectionDelete')}</h3>
            <p style={{ color: 'var(--text-secondary)', marginTop: '0.5rem' }}>{t('collectionDeleteConfirm')}</p>
            <div className="controls" style={{ marginTop: '1.5rem' }}>
              <button
                className="btn btn-primary"
                style={{ background: 'var(--error)', borderColor: 'var(--error)' }}
                onClick={handleDelete}
              >
                {t('delete')}
              </button>
              <button className="btn btn-secondary" onClick={() => setConfirmDelete(false)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
